import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { toast } from 'react-toastify';

import { useGetPostsQuery, useDeletePostMutation } from 'api/post';
import DashboardLayout from 'components/layouts/DashboardLayout';
import DashboardNavbar from 'components/navbar/DashboardNavbar/TopNavbar';
import BasicTable from 'components/common/Table';
import MDBox from 'components/material/MDBox';
import MDTypography from 'components/material/MDTypography';
import MDButton from 'components/material/MDButton';
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from 'api/post';

// Move the dialog component outside to prevent recreation
const CreateCategoryDialog = ({ open, name, onClose, onSubmit, onChange }) => (
  <Dialog open={open} onClose={onClose}>
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogTitle>Adaugă Categorie Nouă</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Introduceți numele pentru noua categorie de postări. Categoria va fi disponibilă pentru
          organizarea postărilor.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Numele categoriei"
          type="text"
          fullWidth
          variant="outlined"
          value={name}
          onChange={onChange}
          placeholder="Ex: Știri, Evenimente, Anunțuri..."
        />
      </DialogContent>
      <DialogActions>
        <MDButton type="submit" color="info">
          Creează
        </MDButton>
        <MDButton type="button" onClick={onClose} color="error" variant="outlined">
          Anulează
        </MDButton>
      </DialogActions>
    </form>
  </Dialog>
);

// Edit Category Dialog Component
const EditCategoryDialog = ({ open, name, onClose, onSubmit, onChange, isLoading }) => (
  <Dialog open={open} onClose={onClose}>
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <DialogTitle>Editează Categoria</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Modificați numele categoriei de postări.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Numele categoriei"
          type="text"
          fullWidth
          variant="outlined"
          value={name}
          onChange={onChange}
          placeholder="Ex: Știri, Evenimente, Anunțuri..."
        />
      </DialogContent>
      <DialogActions>
        <MDButton type="submit" color="info" disabled={isLoading}>
          {isLoading ? 'Se salvează...' : 'Salvează'}
        </MDButton>
        <MDButton type="button" onClick={onClose} color="error" variant="outlined">
          Anulează
        </MDButton>
      </DialogActions>
    </form>
  </Dialog>
);

const PostsScreen = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);
  const { data: posts, isLoading: postsLoading } = useGetPostsQuery();
  const { data: categories, isLoading: categoriesLoading } = useGetCategoriesQuery();
  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdatingCategory }] = useUpdateCategoryMutation();
  const [deleteCategory, { isLoading: isDeletingCategory }] = useDeleteCategoryMutation();
  const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, post: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, name: '' });
  const [editCategoryDialog, setEditCategoryDialog] = useState({
    open: false,
    category: null,
    name: '',
  });
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, category: null });

  useEffect(() => {
    console.log('Categories', categories, categoriesLoading);
  }, [categories]);

  const handleEdit = (post) => {
    navigate(`/dashboard/posts/edit/${post.id}`);
  };

  const handleDeleteClick = (post) => {
    setDeleteDialog({ open: true, post });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePost(deleteDialog.post.id).unwrap();
      toast.success('Postare ștearsă cu succes!');
    } catch (error) {
      toast.error('A apărut o eroare la ștergerea postării.');
    } finally {
      setDeleteDialog({ open: false, post: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, post: null });
  };

  // Category handlers
  const handleCreateCategory = () => {
    setCategoryDialog({ open: true, name: '' });
  };

  const handleCategorySubmit = async () => {
    if (!categoryDialog.name.trim()) {
      toast.error('Numele categoriei este obligatoriu!');
      return;
    }

    try {
      await createCategory({ title: categoryDialog.name }).unwrap();
      toast.success('Categorie creată cu succes!');
      setCategoryDialog({ open: false, name: '' });
    } catch (error) {
      toast.error('A apărut o eroare la crearea categoriei.');
    }
  };

  const handleCategoryChange = (e) => {
    setCategoryDialog({ ...categoryDialog, name: e.target.value });
  };

  const handleCategoryClose = () => {
    setCategoryDialog({ open: false, name: '' });
  };

  // Edit category handlers
  const handleEditCategory = (category) => {
    setEditCategoryDialog({
      open: true,
      category: category,
      name: category.title,
    });
  };

  const handleEditCategorySubmit = async () => {
    if (!editCategoryDialog.name.trim()) {
      toast.error('Numele categoriei este obligatoriu!');
      return;
    }

    try {
      await updateCategory({
        id: editCategoryDialog.category.id,
        data: { title: editCategoryDialog.name },
      }).unwrap();
      toast.success('Categoria a fost actualizată cu succes!');
      setEditCategoryDialog({ open: false, category: null, name: '' });
    } catch (error) {
      toast.error('A apărut o eroare la actualizarea categoriei.');
    }
  };

  const handleEditCategoryChange = (e) => {
    setEditCategoryDialog({ ...editCategoryDialog, name: e.target.value });
  };

  const handleEditCategoryClose = () => {
    setEditCategoryDialog({ open: false, category: null, name: '' });
  };

  const handleDeleteCategoryClick = (category) => {
    setDeleteCategoryDialog({ open: true, category });
  };

  const handleDeleteCategoryConfirm = async () => {
    try {
      await deleteCategory(deleteCategoryDialog.category.id).unwrap();
      toast.success('Categorie ștearsă cu succes!');
      setDeleteCategoryDialog({ open: false, category: null });
    } catch (error) {
      toast.error('A apărut o eroare la ștergerea categoriei.');
      setDeleteCategoryDialog({ open: false, category: null });
    }
  };

  const handleDeleteCategoryCancel = () => {
    setDeleteCategoryDialog({ open: false, category: null });
  };

  const DeleteConfirmationDialog = () => (
    <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
      <DialogTitle>Confirmare ștergere</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Sunteți sigur că doriți să ștergeți postarea "{deleteDialog.post?.title}"?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleDeleteCancel} color="primary">
          Anulează
        </MDButton>
        <MDButton onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
          {isDeleting ? 'Se șterge...' : 'Șterge'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  const DeleteCategoryDialog = () => (
    <Dialog open={deleteCategoryDialog.open} onClose={handleDeleteCategoryCancel}>
      <DialogTitle>Confirmare ștergere categorie</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Sunteți sigur că doriți să ștergeți categoria "{deleteCategoryDialog.category?.title}"?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleDeleteCategoryCancel} color="primary">
          Anulează
        </MDButton>
        <MDButton onClick={handleDeleteCategoryConfirm} color="error" disabled={isDeletingCategory}>
          {isDeletingCategory ? 'Se șterge...' : 'Șterge'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  const postColumns = [
    { Header: '# (ID)', accessor: 'id', align: 'left' },
    { Header: 'Titlu', accessor: 'title', align: 'left' },
    { Header: 'Autor', accessor: 'author', align: 'left' },
    { Header: 'Data', accessor: 'createdAt', align: 'center' },
    { Header: 'Acțiuni', accessor: 'actions', align: 'center' },
  ];

  const categoryColumns = [
    { Header: '# (ID)', accessor: 'id', align: 'left' },
    { Header: 'Nume', accessor: 'name', align: 'left' },
    { Header: 'Data Creării', accessor: 'createdAt', align: 'center' },
    { Header: 'Acțiuni', accessor: 'actions', align: 'center' },
  ];

  useEffect(() => {
    if (posts && !postsLoading) {
      const mappedData = posts?.data?.map((post) => ({
        entry: {
          id: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {post.id}
            </MDTypography>
          ),
          title: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {post.title}
            </MDTypography>
          ),
          author: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {post.author.name}
            </MDTypography>
          ),
          createdAt: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {moment(post.createdAt).format('L')}
            </MDTypography>
          ),
          actions: (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton color="primary" onClick={() => handleEdit(post)}>
                <Icon>edit</Icon>
              </IconButton>
              <IconButton color="error" onClick={() => handleDeleteClick(post)}>
                <Icon>delete</Icon>
              </IconButton>
            </div>
          ),
        },
      }));

      setRows(mappedData);
    }
  }, [posts]);

  useEffect(() => {
    if (categories && !categoriesLoading) {
      const mappedCategories = categories?.map((category) => ({
        entry: {
          id: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {category.id}
            </MDTypography>
          ),
          name: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {category.title}
            </MDTypography>
          ),

          createdAt: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {moment(category.createdAt).format('L')}
            </MDTypography>
          ),
          actions: (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton color="primary" onClick={() => handleEditCategory(category)}>
                <Icon>edit</Icon>
              </IconButton>
              <IconButton color="error" onClick={() => handleDeleteCategoryClick(category)}>
                <Icon>delete</Icon>
              </IconButton>
            </div>
          ),
        },
      }));
      setCategoryRows(mappedCategories);
    }
  }, [categories]);

  if (postsLoading || categoriesLoading) return <p>Loading...</p>;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <DeleteConfirmationDialog />
      <CreateCategoryDialog
        open={categoryDialog.open}
        name={categoryDialog.name}
        onClose={handleCategoryClose}
        onSubmit={handleCategorySubmit}
        onChange={handleCategoryChange}
      />
      <EditCategoryDialog
        open={editCategoryDialog.open}
        name={editCategoryDialog.name}
        onClose={handleEditCategoryClose}
        onSubmit={handleEditCategorySubmit}
        onChange={handleEditCategoryChange}
        isLoading={isUpdatingCategory}
      />
      <DeleteCategoryDialog />

      {/* Categories Section */}
      <Card style={{ marginBottom: '24px' }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          <MDBox>
            <MDTypography variant="h6" gutterBottom>
              Categorii Postări
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDButton
              variant="gradient"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleCreateCategory}
            >
              Adaugă Categorie
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox>
          <BasicTable rows={categoryRows} columns={categoryColumns} pageSize={5} />
        </MDBox>
      </Card>

      {/* Posts Section */}
      <Card>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          <MDBox>
            <MDTypography variant="h6" gutterBottom>
              Postări
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<AddIcon />}
              onClick={() => navigate('/dashboard/posts/create')}
            >
              Adaugă Postare
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox>
          <BasicTable rows={rows} columns={postColumns} pageSize={10} />
        </MDBox>
      </Card>
    </DashboardLayout>
  );
};

export default PostsScreen;
